<template>
  <div class="simplified-html-view" v-if="orderedBlocks.length">
    <div
      v-for="block in orderedBlocks"
      :key="block.id"
      :ref="'block-' + block.id"
      class="block-item"
      :class="[
        'block-type-' + block.type,
        'block-tag-' + block.tag,
        {
          'block-selected': selectedBlockId === block.id,
          'block-grouped': block.group,
          'block-hovered-external': externallyHoveredBlockId === block.id
        }
      ]"
      @click="selectBlock(block.id)"
      @mouseenter="$emit('block-hover', block.id)"
      @mouseleave="$emit('block-hover', null)"
    >
      <!-- Heading blocks -->
      <div v-if="block.type === 'heading'" class="block-content">
        <span class="block-label">{{ blockLabel(block) }}</span>
        <div
          :ref="'editor-' + block.id"
          :class="'editable-heading heading-' + block.tag"
          contenteditable="true"
          @blur="onHtmlEdit(block.id, $event)"
          @keydown="onKeydown(block.id, $event, true)"
          v-html="sanitizeHtml(getBlockValue(block.id))"
        ></div>
      </div>

      <!-- Rich text blocks (p, li, blockquote) -->
      <div v-else-if="block.type === 'richtext'" class="block-content">
        <span class="block-label">{{ blockLabel(block) }}</span>
        <div
          :ref="'editor-' + block.id"
          class="editable-richtext"
          contenteditable="true"
          @blur="onHtmlEdit(block.id, $event)"
          @keydown="onKeydown(block.id, $event, true)"
          v-html="sanitizeHtml(getBlockValue(block.id))"
        ></div>
      </div>

      <!-- Image blocks -->
      <div v-else-if="block.type === 'image'" class="block-content block-image">
        <span class="block-label">{{ blockLabel(block) }}</span>
        <div class="image-preview">
          <img v-if="getBlockValue(block.id) && !failedImages[block.id]" :src="getBlockValue(block.id)" class="preview-img" @error="onImgError(block.id)" />
          <v-icon v-else large color="grey lighten-1">mdi-image-outline</v-icon>
        </div>
        <div class="image-src-row mt-1">
          <v-text-field
            :ref="'editor-' + block.id"
            :value="getBlockValue(block.id)"
            @change="onFieldEdit(block.id, $event)"
            @keydown.native.enter="onFieldKeydownEnter(block.id, $event)"
            :label="$t('custom_html_block_image_src')"
            dense
            outlined
            hide-details
          ></v-text-field>
          <v-tooltip top v-if="isAllowed('gallery_view')">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                color="primary"
                @click.stop="openGallery(block.id)"
              >
                <v-icon>mdi-image-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('cmstool_select_image_gallery') }}</span>
          </v-tooltip>
        </div>
      </div>

      <!-- Link blocks (text part) -->
      <div v-else-if="block.type === 'text' && block.group && block.group.startsWith('a_')" class="block-content">
        <span class="block-label">{{ blockLabel(block) }}</span>
        <v-text-field
          :ref="'editor-' + block.id"
          :value="getBlockValue(block.id)"
          @change="onFieldEdit(block.id, $event)"
          @keydown.native.enter="onFieldKeydownEnter(block.id, $event)"
          :label="$t('custom_html_block_link_text')"
          dense
          outlined
          hide-details
        ></v-text-field>
      </div>

      <!-- Link blocks (url part) -->
      <div v-else-if="block.type === 'url'" class="block-content">
        <span class="block-label">{{ blockLabel(block) }}</span>
        <v-text-field
          :ref="'editor-' + block.id"
          :value="getBlockValue(block.id)"
          @change="onFieldEdit(block.id, $event)"
          @keydown.native.enter="onFieldKeydownEnter(block.id, $event)"
          :label="$t('custom_html_block_link_url')"
          dense
          outlined
          hide-details
        ></v-text-field>
      </div>

      <!-- Plain text blocks (button, td, div, span, etc.) -->
      <div v-else class="block-content">
        <span class="block-label">{{ blockLabel(block) }}</span>
        <div
          :ref="'editor-' + block.id"
          class="editable-text"
          contenteditable="true"
          @blur="onHtmlEdit(block.id, $event)"
          @keydown="onKeydown(block.id, $event, true)"
          v-html="sanitizeHtml(getBlockValue(block.id))"
        ></div>
      </div>

      <!-- Delete button -->
      <v-btn
        icon
        x-small
        class="block-delete-btn"
        @click.stop="deleteBlock(block)"
      >
        <v-icon x-small color="grey">mdi-delete-outline</v-icon>
      </v-btn>
    </div>

    <select-image-dialog
      :display-select-image-dialog="activeGalleryBlockId !== null"
      @update:displaySelectImageDialog="closeGallery"
      @gallery-selected="onGallerySelected"
      :app-tags="['stc-editor-image']"
      :isAllowedEdit="isAllowed('gallery_edit')"
    ></select-image-dialog>

    <!-- Empty state -->
  </div>
  <div v-else class="simplified-html-empty pa-6">
    <div class="template-picker">
      <h3 class="picker-title mb-4">{{ $t('custom_html_template_picker_title') }}</h3>

      <v-alert
        v-if="!isEnglishReference"
        type="warning"
        text
        dense
        class="template-language-warning text-body-2"
      >
        {{ $t('custom_html_template_non_english_warning') }}
        <router-link :to="{ name: 'config-global', query: { editLanguage: '1' } }">{{ $t('custom_html_template_change_language_link') }}</router-link>
      </v-alert>

      <div class="template-cards">
        <div
          v-for="template in templates"
          :key="template.id"
          class="template-card"
          :class="{ disabled: !isEnglishReference }"
          @click="isEnglishReference && $emit('template-selected', template.html)"
        >
          <v-icon color="primary" class="template-icon mb-2">{{ template.icon }}</v-icon>
          <div class="template-name font-weight-bold">{{ $t(template.nameKey) }}</div>
          <div class="template-desc grey--text">{{ $t(template.descKey) }}</div>
        </div>
      </div>

      <div class="text-center mt-4">
        <a class="scratch-link grey--text text-caption" @click="$emit('start-scratch')">
          {{ $t('custom_html_template_picker_or_scratch') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getOrderedBlocks } from '@/utils/htmlBlockExtractor'
import htmlTemplates from '@/templates/htmlTemplates'
import { SelectImageDialog } from '@scantrust/st-gallery'
import DOMPurify from 'dompurify'

export default {
  name: 'SimplifiedHtmlView',
  components: {
    'select-image-dialog': SelectImageDialog
  },
  props: {
    tabId: { type: String, required: true },
    selectedBlockId: { type: String, default: null }
  },
  data () {
    return {
      failedImages: {},
      templates: htmlTemplates,
      activeGalleryBlockId: null
    }
  },
  computed: {
    ...mapGetters(['defaultLanguage', 'getHtmlBlocks', 'getBlocksMeta', 'hoveredBlockId', 'hoverSource', 'isAllowed']),

    isEnglishReference () {
      return this.defaultLanguage === 'en'
    },

    blocks () {
      return this.getHtmlBlocks(this.tabId)
    },

    meta () {
      return this.getBlocksMeta(this.tabId)
    },

    orderedBlocks () {
      return getOrderedBlocks(this.blocks, this.meta)
    },

    externallyHoveredBlockId () {
      return this.hoverSource === 'preview' ? this.hoveredBlockId : null
    }
  },
  methods: {
    getBlockValue (blockId) {
      const block = this.blocks[blockId]
      if (!block) return ''
      return block[this.defaultLanguage] || ''
    },

    // Block content originates from arbitrary custom HTML (authored by one user,
    // potentially viewed by a ScanTrust admin). It is rendered here via v-html in
    // the app's own origin, so it must be sanitized to neutralise XSS payloads
    // (e.g. <img onerror>) before display. The stored HTML is left untouched so
    // the sandboxed preview iframe keeps full fidelity.
    sanitizeHtml (html) {
      return DOMPurify.sanitize(html || '')
    },

    blockLabel (block) {
      const labels = {
        heading: this.$t('custom_html_block_heading'),
        richtext: this.$t('custom_html_block_paragraph'),
        text: this.$t('custom_html_block_text'),
        image: this.$t('custom_html_block_image_src'),
        url: this.$t('custom_html_block_link_url')
      }
      // For link text blocks
      if (block.type === 'text' && block.group && block.group.startsWith('a_')) {
        return this.$t('custom_html_block_link_text')
      }
      if (block.tag === 'button') {
        return this.$t('custom_html_block_button')
      }
      return labels[block.type] || block.tag
    },

    selectBlock (blockId) {
      this.$emit('update:selectedBlockId', this.selectedBlockId === blockId ? null : blockId)
    },

    onHtmlEdit (blockId, event) {
      const value = event.target.innerHTML.trim()
      this.emitEdit(blockId, value)
    },

    onFieldEdit (blockId, value) {
      // Reset image error state when URL changes
      if (this.failedImages[blockId]) {
        this.$delete(this.failedImages, blockId)
      }
      this.emitEdit(blockId, value)
    },

    onKeydown (blockId, event, useInnerHtml) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        const value = useInnerHtml
          ? event.target.innerHTML.trim()
          : event.target.textContent.trim()
        this.emitEdit(blockId, value)
        event.target.blur()
      }
    },

    onFieldKeydownEnter (blockId, event) {
      this.onFieldEdit(blockId, event.target.value)
      event.target.blur()
    },

    openGallery (blockId) {
      this.activeGalleryBlockId = blockId
    },

    closeGallery () {
      this.activeGalleryBlockId = null
    },

    onGallerySelected (url) {
      if (this.activeGalleryBlockId) {
        this.onFieldEdit(this.activeGalleryBlockId, url)
      }
      this.closeGallery()
    },

    deleteBlock (block) {
      this.$emit('block-deleted', { blockId: block.id })
    },

    emitEdit (blockId, value) {
      const current = this.getBlockValue(blockId)
      if (value !== current) {
        this.$emit('block-edited', { blockId, value })
      }
    },

    onImgError (blockId) {
      this.$set(this.failedImages, blockId, true)
    },

    scrollBlockIntoView (blockId) {
      const ref = this.$refs['block-' + blockId]
      const el = Array.isArray(ref) ? ref[0] : ref
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    },

    focusBlock (blockId) {
      const ref = this.$refs['editor-' + blockId]
      const target = Array.isArray(ref) ? ref[0] : ref
      if (!target) return
      // Vuetify v-text-field exposes .focus(); contenteditable divs use the DOM .focus()
      if (target.$el && typeof target.focus === 'function') {
        target.focus()
      } else if (typeof target.focus === 'function') {
        target.focus()
      }
    }
  }
}
</script>

<style lang="sass">
.simplified-html-view
  padding: 16px
  overflow-y: auto
  height: 100%

.simplified-html-view .block-item
  position: relative
  padding: 8px 12px
  margin-bottom: 4px
  border-radius: 4px
  border: 2px solid transparent
  cursor: pointer
  transition: border-color 0.15s, background 0.15s
  &:hover
    background: #f8f8f8
    border-color: #e0e0e0
  &.block-selected
    border-color: #1976d2
    background: #e3f2fd
  &.block-hovered-external
    border-color: #64b5f6
    background: #f1f8ff

.simplified-html-view .block-label
  display: inline-block
  font-size: 10px
  font-weight: 600
  color: #999
  text-transform: uppercase
  letter-spacing: 0.5px
  margin-bottom: 2px

.simplified-html-view .block-delete-btn
  position: absolute
  top: 4px
  right: 4px
  opacity: 0
  transition: opacity 0.15s

.simplified-html-view .block-item:hover .block-delete-btn
  opacity: 1

// Editable content styles
.simplified-html-view .editable-heading,
.simplified-html-view .editable-richtext,
.simplified-html-view .editable-text
  outline: none
  padding: 4px 0
  border-bottom: 1px dashed transparent
  transition: border-color 0.15s
  &:focus
    border-bottom-color: #1976d2

// Heading sizes
.simplified-html-view .heading-h1
  font-size: 1.8em
  font-weight: bold
  line-height: 1.3

.simplified-html-view .heading-h2
  font-size: 1.5em
  font-weight: bold
  line-height: 1.3

.simplified-html-view .heading-h3
  font-size: 1.25em
  font-weight: bold
  line-height: 1.3

.simplified-html-view .heading-h4,
.simplified-html-view .heading-h5,
.simplified-html-view .heading-h6
  font-size: 1.1em
  font-weight: bold
  line-height: 1.3

// Richtext
.simplified-html-view .editable-richtext
  font-size: 14px
  line-height: 1.6

// Text
.simplified-html-view .editable-text
  font-size: 14px
  line-height: 1.5

// Image
.simplified-html-view .block-image .image-preview
  display: flex
  align-items: center
  justify-content: center
  min-height: 60px
  max-height: 150px
  overflow: hidden
  background: #f5f5f5
  border-radius: 4px
  margin-bottom: 4px

.simplified-html-view .preview-img
  max-width: 100%
  max-height: 150px
  object-fit: contain

.simplified-html-view .block-image .image-src-row
  display: flex
  align-items: center
  gap: 4px

// Grouped blocks (link text + url) — reduce gap between them
.simplified-html-view .block-grouped + .block-grouped
  margin-top: -4px

// Template picker / empty state
.simplified-html-empty
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  height: 100%
  overflow-y: auto

.simplified-html-empty .template-picker
  max-width: 500px
  width: 100%

.simplified-html-empty .picker-title
  font-size: 16px
  font-weight: 600
  color: #333
  text-align: center

.simplified-html-empty .template-cards
  display: flex
  flex-direction: column
  gap: 10px

.simplified-html-empty .template-card
  background: #fff
  border: 1px solid #e0e0e0
  border-radius: 8px
  padding: 16px
  cursor: pointer
  transition: border-color 0.15s, box-shadow 0.15s
  display: flex
  align-items: center
  gap: 14px
  &:hover
    border-color: #1976d2
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.12)
  &.disabled
    cursor: not-allowed
    opacity: 0.5
    &:hover
      border-color: #e0e0e0
      box-shadow: none

.simplified-html-empty .template-icon
  flex-shrink: 0
  font-size: 28px !important

.simplified-html-empty .template-name
  font-size: 14px
  line-height: 1.3
  margin-bottom: 2px

.simplified-html-empty .template-desc
  font-size: 12px
  line-height: 1.4

.simplified-html-empty .scratch-link
  cursor: pointer
  text-decoration: underline
  &:hover
    color: #1976d2 !important
</style>
